import { useEffect, useRef, useState } from 'react';
import { components } from "../components";
import { isEmpty } from 'lodash';
import utils from '../helpers/utils';

interface TyroJSInstance {
    init: (paySecret: string) => Promise<any>;
    createPayForm: (config: any) => any;
    submitPay: () => Promise<any>;
    fetchPayRequest: () => Promise<{ status: string }>;
}

interface ErrorState {
    type?: string;
    errorCode?: string;
    message?: string;
}

interface TyroJSRef {
    tyroJSInstance?: TyroJSInstance;
}

interface TyroPaymentProps {
    paySecret: string;
    isDefaultSave: boolean;
    applePay: boolean;
    onMessage: (event: MessageEvent) => void;
}


export default function TyroPayment({
    paySecret,
    isDefaultSave,
    applePay,
    onMessage
}: TyroPaymentProps): JSX.Element {
    const tyroJSRef = useRef<TyroJSRef | null>(null);
    const [configuration, setConfiguration] = useState<any>({
        theme: 'default',
        styleProps: { bodyBackgroundColor: "var(--main-background)" },
        options: {
            applePay: { enabled: false },
            googlePay: { enabled: false },
            creditCardForm: { enabled: true },
        },
    });
    const [error, setError] = useState<ErrorState>({});
    const [loading, setLoading] = useState(false);
    const [libraryReady, setLibraryReady] = useState(false);
    const [payRequestReady, setPayRequestReady] = useState(false);
    const [payFormReady, setPayFormReady] = useState(false);
    const [saveForFuture, setSaveForFuture] = useState(true);
    const [submittingOverlay, setSubmittingOverlay] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [buttonStyle, setButtonStyle] = useState<React.CSSProperties>({
        justifyContent: 'center',
        marginBottom: 10,
        borderRadius: '5px',
        width: '100%',
        backgroundColor: '#F3C910',
        border: '0px',
        color: 'white',
        padding: '10px',
        marginTop: '10px',
        fontSize: '20px',
    });
    const { theme, styleProps, options } = configuration;
    const { liveMode } = utils.getApiModeConfig();
    const buttonBorderRadius = '10px';
    const buttonBackgroundColor = "ff161f";

    // STEP 1, Attach TyroJS Library
    useEffect(() => {
        const script = document.createElement('script');
        script.id = 'tyro-js-library';
        script.src = 'https://pay.connect.tyro.com/v1/tyro.js';
        script.crossOrigin = 'anonymous';
        script.async = true;
        script.onload = () => setLibraryReady(true);
        document.body.appendChild(script);

        if (buttonBorderRadius) {
            setButtonStyle((prevStyle) => ({
                ...prevStyle,
                borderRadius: buttonBorderRadius,
            }));
        }

        if (buttonBackgroundColor) {
            setButtonStyle((prevStyle) => ({
                ...prevStyle,
                backgroundColor: '#' + buttonBackgroundColor,
            }));
        }

        setLoading(true);
    }, []);

    // STEP 2, Fetch the Pay Secret
    useEffect(() => {
        setError({});
        if (!isEmpty(paySecret)) {
            setConfiguration({
                ...configuration,
                paySecret: paySecret,
                options: {
                    applePay: {
                        enabled: applePay,
                        totalLabel: "Carl's Jr. Australia",
                    },
                    googlePay: { enabled: false },
                    creditCardForm: { enabled: applePay ? false : true },
                },
            });
        }
    }, [paySecret]);

    // STEP 3, Load the Pay Request into TyroJS
    useEffect(() => {
        if (libraryReady && paySecret?.length) {
            initPayRequest();
        }
    }, [libraryReady, paySecret]);

    // Initialize Tyro.js with the Pay Request
    async function initPayRequest() {
        setPayRequestReady(false);
        setLoading(true);
        setPayFormReady(false);
        setSubmitting(false);
        try {
            // @ts-ignore global Tyro from SDK
            tyroJSRef.current!.tyroJSInstance = Tyro({ liveMode });
            await tyroJSRef.current!.tyroJSInstance!.init(paySecret);

            const payFormElement = document.getElementById('tyro-pay-form');
            if (payFormElement) {
                payFormElement.innerHTML = '';
            }
            setPayRequestReady(true);
        } catch (err: any) {
            console.error(err.toString());
            setLoading(false);
        }
    }

    // STEP 4, Embed the Pay Form
    useEffect(() => {
        if (libraryReady && payRequestReady && !payFormReady) {
            initPayForm();
        }
    }, [payRequestReady]);

    async function initPayForm() {
        setPayFormReady(false);
        setSubmitting(false);
        try {
            if (!tyroJSRef.current) return;

            const payFormElement = document.getElementById('tyro-pay-form');
            if (!payFormElement) throw new Error(`Pay Form is not mounted`);

            payFormElement.innerHTML = '';

            const payForm = tyroJSRef.current!.tyroJSInstance!.createPayForm({
                theme,
                styleProps,
                options,
            });

            payForm.setWalletPaymentBeginListener((paymentType: string) => {
                setSubmittingOverlay(true);
                if (paymentType === 'APPLE_PAY') {
                    // optionally do something specific to Apple Pay
                } else if (paymentType === 'GOOGLE_PAY') {
                    // optionally do something specific to Google Pay
                }
            });

            payForm.setWalletPaymentCancelledListener((paymentType: string) => {
                setSubmittingOverlay(false);
                if (paymentType === 'APPLE_PAY') {
                    // optionally do something specific to Apple Pay
                } else if (paymentType === 'GOOGLE_PAY') {
                    // optionally do something specific to Google Pay
                }
            });

            payForm.setWalletPaymentCompleteListener(
                (paymentType: string, err: Error | null) => {
                    if (err) {
                        console.error(err.toString());
                        setSubmittingOverlay(false);
                    } else {
                        getPaymentResult();
                    }
                }
            );

            payForm.inject('#tyro-pay-form');
            setPayFormReady(true);
            setLoading(false);
        } catch (err: any) {
            console.error(err.toString());
            setLoading(false);
        }
    }

    // STEP 5, Submit the Pay Form
    async function submitPayForm(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        setError({});
        setSubmitting(true);
        setLoading(true);
        try {
            await tyroJSRef.current!.tyroJSInstance!.submitPay();
            await getPaymentResult();
        } catch (err: any) {
            if (!(err?.type === 'CLIENT_VALIDATION_ERROR' && !err?.errorCode)) {
                console.error(err.toString());
            }
            setSubmitting(false);
            setLoading(false);
        }
    }

    async function getPaymentResult() {
        const payRequest = await tyroJSRef.current!.tyroJSInstance!.fetchPayRequest();
        if (payRequest.status === 'SUCCESS') {
            const data = { success: true, saveForFuture };
            // @ts-ignore (React Native WebView)
            onMessage(JSON.stringify(data));
        } else {
            console.error(error.toString());
            setLoading(false);
        }
        setSubmittingOverlay(false);
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                paddingTop: '15px',
            }}
        >
            <div ref={tyroJSRef as any}>
                {error.type ? (
                    <div className={'error-container'}>
                        <p>({error.errorCode ?? 'UNKNOWN_ERROR'}) {error.message}</p>
                    </div>
                ) : null}
                <>
                    <form id="pay-form">
                        <div
                            id="pay-form-submitting-overlay"
                            style={{
                                display: submittingOverlay ? 'block' : 'none',
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                backgroundColor: 'rgba(255,255,255,0.5)',
                            }}
                        >
                            ... Submitting ...
                        </div>

                        <div
                            id="tyro-pay-form"
                            style={{ visibility: payRequestReady ? 'visible' : 'hidden' }}
                        ></div>

                        <label
                            style={{
                                marginLeft: '8px',
                                visibility:
                                    payRequestReady && isDefaultSave && !applePay
                                        ? 'visible'
                                        : 'hidden',
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={saveForFuture}
                                onChange={(e) => setSaveForFuture(e.target.checked)}
                                style={{ marginRight: '8px' }}
                            />
                            <span>Save this detail for future purchase</span>
                        </label>

                        <button
                            className="t12"
                            id="pay-form-submit"
                            onClick={submitPayForm}
                            style={{
                                ...buttonStyle,
                                visibility: payRequestReady && !applePay ? 'visible' : 'hidden',
                            }}
                            disabled={submitting}
                        >
                            Add Card
                        </button>
                    </form>
                </>
            </div>

            {loading ? (
                <div className="flex fixed inset-0 items-center justify-center bg-opacity-80 bg-white">
                    <components.Loader />
                </div>
            ) : null}
        </div>
    );
};